import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { calendarData, calendarDataMobile } from './calendarData';
import Modal from "./Modal";
import logo2026 from "../img/2026/aldi-logo.png";
import { Link } from "react-router-dom";
import { getUserData } from '../utils/helper';
import { buildGameUrl } from '../utils/gameUrl';
import UserContext from '../utils/userContext';
import festiveFetch from '../img/badges/festive-fetch.png';
import santaStack from '../img/badges/santa-stack.png';
import pythonPursuit from '../img/badges/python-pursuit.png';
import matchWin from '../img/badges/match-win.png';
import christmasConnections from '../img/badges/christmas-connections.png';
import whackAPana from '../img/badges/whack-a-pana.png';
import merryMindMaster from '../img/badges/merry-mind-master.png';
import hamHurl from '../img/badges/ham-hurl.png';
import snackSprint from '../img/badges/snack-sprint.png';
import prawnBbq from '../img/badges/prawn-bbq.png';
import santaHeist from '../img/badges/santa-heist.png';
import sparkles from '../img/2026/sparkles.png';
import sparklesSmall from '../img/2026/sparkles-small.png';
import AlertModal from "./AlertModal";
import ConfirmedModal from "./ConfirmedModal";

const TEXTURED_DOOR_DAYS = new Set([2, 9, 10, 20, 22, 23, 24]);
const SPARKLE_DOOR_POSITIONS = {
    5: 'bottom',
    6: 'top',
    8: 'bottom',
    13: 'top',
    17: 'bottom',
};

const BADGES_BY_DAY = {
    1: santaHeist,
    2: prawnBbq,
    3: matchWin,
    4: christmasConnections,
    5: santaStack,
    6: snackSprint,
    7: santaHeist,
    8: prawnBbq,
    9: merryMindMaster,
    10: hamHurl,
    11: festiveFetch,
    12: pythonPursuit,
    13: matchWin,
    14: prawnBbq,
    15: santaStack,
    16: whackAPana,
    17: santaHeist,
    18: christmasConnections,
    19: snackSprint,
    20: merryMindMaster,
    21: hamHurl,
    22: prawnBbq,
    23: santaHeist,
    24: matchWin,
};

const getPreviewGameData = () => {
    if (process.env.NODE_ENV === 'production') {
        return {};
    }

    const params = new URLSearchParams(window.location.search);
    const previewModal = params.get('previewModal');
    if (!['complete', 'score', 'quit'].includes(previewModal)) {
        return {};
    }

    const requestedDay = Number(params.get('day')) || 1;
    const day = Math.min(24, Math.max(1, requestedDay));
    const calendarItem = calendarData.data.find(item => item.day === day);

    return {
        ...calendarItem,
        game: calendarItem?.game || 'preview',
        day,
        isClosedConfirmPopup: previewModal === 'quit'
    };
};

const Calendar = ({ userData, setUserData }) => {
    const DEBUG_MODE = false; // Set to true to enable all days for testing purposes
    const [isMobile, setIsMobile] = useState(false);
    const [openActive, setOpenActive] = useState(null);
    const [loading, setLoading] = useState(false);
    const [gameContent, setGameContent] = useState('');
    const gameIframeRef = useRef(null);
    const { userId } = useContext(UserContext);
    const [gameData, setGameData] = useState(getPreviewGameData)

    const returnToCalendar = useCallback(() => {
        setLoading(false);
        setOpenActive(null);
        setGameContent('');
        setGameData({});
    }, []);

    useEffect(() => {
        const handleGameMessage = (event) => {
            if (
                event.origin !== window.location.origin ||
                !gameIframeRef.current ||
                event.source !== gameIframeRef.current.contentWindow ||
                event.data?.type !== 'advent:return-to-calendar'
            ) {
                return;
            }

            // The calendar is underneath the game; no navigation is needed.
            returnToCalendar();
        };

        window.addEventListener('message', handleGameMessage);
        return () => window.removeEventListener('message', handleGameMessage);
    }, [returnToCalendar]);

    const sortDesktop = (a, b) => {
        const sortingArr = [13, 9, 18, 5, 6, 22, 7, 16, 2, 24, 17, 12, 1, 20, 15, 11, 3, 8, 19, 14, 21, 4, 23, 10];
        return sortingArr.indexOf(a.day) - sortingArr.indexOf(b.day);
    };
    const sortMobile = (a, b) => {
        const sortingArr = [13, 9, 18, 15, 5, 12, 22, 8, 11, 19, 4, 1, 21, 14, 6, 23, 3, 7, 20, 16, 2, 10, 24, 17];
        return sortingArr.indexOf(a.day) - sortingArr.indexOf(b.day);
    };
    const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
    };


    const getCurrentDay = () => {
        const p = new URLSearchParams(window.location.search).get('day');
        if (p) {
            const n = Number(p);
            if (!Number.isNaN(n)) return Math.min(24, Math.max(1, n));
        }
        if (process.env.REACT_APP_FORCE_DAY) {
            const n = Number(process.env.REACT_APP_FORCE_DAY);
            if (!Number.isNaN(n)) return Math.min(24, Math.max(1, n));
        }

        return new Date().getDate();
    };

    const currentDay = getCurrentDay();

    {/* for testing */ }

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {

            window.__completeDay = (d) => {
                setUserData((prev) => {
                    const prevArr = Array.isArray(prev) ? prev : [];
                    const without = prevArr.filter(e => e.tournament_id !== String(d));
                    return [...without, { tournament_id: String(d), is_valid: true }];
                });
            };

            window.__resetProgress = () => setUserData([]);
        }
    }, [setUserData]);

    {/* END for testing */ }

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        if (!document.body.classList.contains('calendar-page')) {
            document.body.classList.add('calendar-page')
        }
        return () => {
            window.removeEventListener('resize', handleResize);
            if (document.body.classList.contains('calendar-page')) {
                document.body.classList.remove('calendar-page')
            }
        };
    }, []);

    const handleOpenTile = (day) => {
        if (!openActive) {
            setOpenActive(day);
        }
        if (openActive === day) {
            setOpenActive(null);
        } else {
            setOpenActive(day);
        }
    };

    const closePopup = (item) => {
        setLoading(false);
        setOpenActive(null);
        setGameContent('');
        setGameData(item);
        if (item.gameType && item.gameType === 'quiz') {
            getUserData(userId)
                .then(data => {
                    if (data && data.length > 0) {
                        setUserData(data)
                    }
                })
        }
    }



    const handleClearGameData = () => {
        setGameData({});
    }

    const handleCloseGame = (item) => {
        setGameData({ ...item, isClosedConfirmPopup: true });
    }
    const modalLoadingGame = () => {
        if (!gameData?.game) {
            return;
        }
        handleLoadGame(gameData.game, gameData);
    }


    let loaderContent = (
        <>
            <div className="text-center mx-auto">
                {gameContent
                    ? (<>
                        {gameContent}
                    </>)
                    : (<>
                        <Link to="/" className="inline-block mx-auto logo mb-4">
                            <img src={logo2026} alt="ALDI" className="w-[58px]" />
                        </Link>
                        <div className={`loading mx-auto rounded-[20px] bg-[rgba(0,0,0,0.5)] h-[6px] w-[120px]`}>
                            <div className="progress-bar rounded-[20px] h-[6px] w-[120px]" />
                        </div>
                    </>)
                }
            </div>
            <div className="pt-24" />
        </>
    );


    const completedLayout = (cellType, mobile) => {
        const D = {
            small: {
                wrap: 'absolute inset-0 flex items-center justify-center z-10 pt-[12px]',
                body: 'flex flex-col items-center gap-0'
            },
            wide: {
                wrap: 'absolute inset-0 flex items-center justify-center px-2 z-10',
                body: 'flex items-center flex-col gap-1'
            },
            tall: {
                wrap: 'absolute inset-0 flex items-center justify-center px-2 z-10',
                body: 'flex flex-col items-center gap-1'
            },
            big: {
                wrap: 'absolute inset-0 flex items-center justify-center px-6 z-10',
                body: 'flex flex-col items-center gap-1'
            },
        };
        const M = D;
        const map = mobile ? M : D;
        return map[cellType] || map.small;
    };


    const CompletedOverlay = ({ item, mobile }) => {
        const lay = completedLayout(item.cellType, mobile);
        return (
            <div
                className={`title-completed ${lay.wrap} pointer-events-none
                        opacity-100`}
            >
                <div className={lay.body}>
                    <span className="calendar-thumb-icon" aria-hidden="true" />
                    <span className="font-bold text-[clamp(14px,1.6vw,22px)] leading-none text-[#FFDB9E] text-base">
                        Well done!
                    </span>
                </div>
            </div>
        );
    };


    const replayLayout = (cellType, mobile) => {
        const D = {
            small: {
                container: 'relative flex items-start justify-end p-2 gap-2 ',
                banner: '',
                text: 'text-[18px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                badge: 'hidden',
                replayTitle:'hidden',
                replayText: 'hidden'

            },
            wide: {
                container: 'relative !flex-col flex items-center justify-between px-2 mb-5px ',
                banner: 'absolute left-0 right-0 top-0 p-3',
                text: 'text-[18px] justify-center flex items-center flex-row gap-1',
                badge: 'inline-block',

            },
            tall: {
                container: 'relative flex flex-row items-center justify-between px-2 gap-2 ',
                banner: 'absolute left-0 top-0 bottom-0 w-[44%] p-3',
                text: 'text-[18px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                badge: 'hidden',

            },
            big: {
                container: 'flex flex-col items-start justify-center gap-1 px-6 pt-[15px] pb-[10px]',
                banner: '',
                text: 'text-[18px] flex items-center gap-1',
                badge: 'block',

            },
        };

        const M = {
            small: {
                container: D.small.container,
                banner: D.small.banner,
                text: D.small.text,
                badge: 'hidden',
                replayTitle:'hidden',
                replayText: 'hidden'
            },
            wide: {
              container: D.wide.container,
              banner: D.wide.banner,
              text: D.wide.text,
              badge: 'hidden',
            },
            tall: {
                container: D.tall.container
                .replace('px-2', 'px-1')
                .replace('gap-2', 'gap-0'),
                banner: D.tall.banner,
                text: D.tall.text,
                badge: 'hidden',
            },
            big: {
                container: D.big.container,
                banner: D.big.banner,
                text: D.big.text,
                badge: D.big.badge + ' hidden',
            },
          };

        const map = mobile ? M : D;
        return map[cellType] || map.small;
    };


    const ReplayOverlay = ({ item, mobile }) => {
        const lay = replayLayout(item.cellType, mobile);

        return (
            <button
                className={`title-page absolute inset-0 z-0 ${lay.container}`}
                onClick={() => handleLoadGame(item.game, item)}
            >
                <div className={`bg-[rgba(255,255,255,0.15)] rounded-[8px] text-center`}>
                    <div className={`${lay.replayTitle} font-bold leading-tight text-[14px] mb-[5px]`}>
                        Thanks for playing!
                    </div>
                    <div className={`${lay.replayText} leading-tight opacity-90 text-[10px]`}>
                        Your score has been submitted for today’s leaderboard
                    </div>
                </div>
                {renderBadge(item.day) && (
                    <img
                        src={renderBadge(item.day)}
                        alt=""
                        className={`${lay.badge} calendar-game-badge calendar-game-badge-${item.cellType}`}
                        aria-hidden="true"
                    />
                )}
                <span className={`${lay.text} flex items-center center`}>
                    <span className="calendar-game-icon" aria-hidden="true" />
                     <span> Play again</span>
                </span>
            </button>
        );
    };

    const playLayout = (cellType, mobile) => {
        const D = {
            small: {
                container: 'relative flex items-start justify-end p-2 gap-2 ',
                banner: '',
                icon: '',
                text: 'text-[18px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                badge: 'hidden',
                coverContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex justify-center w-full text-xs xl:text-[15px] flex-col items-center',
                coverText: 'max-w-[80%] mx-auto leading-tight whitespace-nowrap',
                coverSmallText: 'Play now!',
                playedText: 'Well done!',
                playedIcon: 'block before:block before:bg-background-welldone before:bg-contain before:bg-no-repeat before:w-[25px] before:h-[30px]',
                playedClass: 'text-[14px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                playedContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex justify-center w-full text-xs xl:text-[15px] flex-col items-center',
                coverIcon: (item) =>
                    `block before:block before:bg-contain before:bg-center before:bg-no-repeat before:w-[54px] before:h-[38px] mb-[2px] ${
                        item?.activeDayBg || 'before:bg-start-day-icon'
                    }`,
                coverBanner: 'hidden',
                showSub: false,
                showBadge: false,
            },
            wide: {
                container: 'relative !flex-row flex items-center justify-between px-2 xl:px-4 gap-2 ',
                banner: 'absolute left-0 right-0 top-0 p-3',
                icon: '',
                text: 'text-[18px] justify-center w-1/2 flex items-center flex-col gap-1',
                badge: 'before:w-[80px] before:h-[72px] inline-block',
                coverContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex items-center w-full text-xs lg:text-[16px] flex-row items-center',
                coverText: 'max-w-[50px] ml-[3px] leading-tight',
                coverSmallText: 'Play now!',
                playedText: 'Well done!',
                playedIcon: 'block before:block before:bg-background-welldone before:bg-contain before:bg-no-repeat before:w-[25px] before:h-[30px]',
                playedClass: 'text-[14px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                playedContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex justify-center w-full text-xs xl:text-[15px] flex-col items-center',
                coverIcon: 'hidden',
                coverBanner: (item) =>
                    `block before:block before:bg-contain before:bg-center before:bg-no-repeat before:w-[80px] before:h-[60px] ml-[15px] mb-[8px] order-[-1] ${
                        item?.activeDayBg || 'before:bg-start-day-icon'
                    }`,
                showSub: false,
                showBadge: true,
            },
            tall: {
                container: 'relative flex flex-row xl:flex-col items-center justify-between xl:justify-center px-2 xl:px-2 gap-2 ',
                banner: 'absolute left-0 top-0 bottom-0 w-[44%] p-3',
                icon: '',
                text: 'text-[18px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                badge: 'before:w-[77px] before:h-[75px]',
                coverContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex justify-center w-full text-[16px] flex-col items-center',
                coverText: 'max-w-[50px] ml-[5px] leading-tight',
                coverSmallText: 'Play now!',
                playedIcon: 'block before:block before:bg-background-welldone before:bg-contain before:bg-no-repeat before:w-[25px] before:h-[30px]',
                playedText: 'Well done!',
                playedClass: 'text-[14px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                playedContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex justify-center w-full text-xs xl:text-[15px] flex-col items-center',
                coverIcon: 'hidden',
                coverBanner: (item) =>
                    `block before:block before:bg-contain before:bg-no-repeat before:bg-center before:w-[80px] before:h-[60px] order-[-1] xl:mb-[15px] ${
                        item?.activeDayBg || 'before:bg-start-day-icon'
                    }`,
                showSub: false,
                showBadge: true,
            },
            big: {
                container: 'flex flex-col items-start justify-center gap-2 px-6 pt-[15px] pb-[10px]',
                banner: '',
                icon: '',
                text: 'text-[18px] flex items-center gap-1',
                badge: 'before:w-[80px] before:h-[77px]',
                coverContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex justify-center w-full text-[16px] flex-col items-center',
                coverText: '',
                coverSmallText: 'Play now!',
                playedText: 'Well done!',
                playedIcon: 'block before:block before:bg-background-welldone before:bg-contain before:bg-no-repeat before:w-[25px] before:h-[30px]',
                playedClass: 'text-[14px] justify-center w-full flex items-center flex-col gap-1 flex justify-center',
                playedContainer: 'title absolute inset-0 text-[#BB0114] font-bold flex justify-center w-full text-xs xl:text-[15px] flex-col items-center',
                coverIcon: 'hidden',
                coverBanner: (item) =>
                    `block before:block before:bg-contain before:bg-no-repeat before:bg-center
              ${item?.activeDayBg || 'before:bg-start-day-icon'}
              ${item?.activeDayStyle?.size || ' before:w-[126px] before:h-[96px]'}
              ${item?.activeDayStyle?.extra || ' '}
        `,
                showSub: false,
                showBadge: true,
            },
        };

        const M = {
            small: { ...D.small },
            wide: { ...D.wide },
            tall: { ...D.tall },
            big: { ...D.big },
        };

        const map = mobile ? M : D;
        return map[cellType] || map.small;
    };

    const handleLoadGame = (game, item) => {
        const gameUrl = buildGameUrl(game, item, userId, process.env.PUBLIC_URL);

        if (isMobile && game === '07') {
            window.open(gameUrl, '_blank', 'noreferrer');
            setOpenActive(null);
        } else {
            setLoading(true);
            setGameContent(<div className="game-container p-[4px]">
                <button
                    type="button"
                    onClick={() => handleCloseGame(item)}
                    className="btn btn-header btn-utility absolute top-0 right-0 z-[1]"
                    aria-label="Close game"
                >
                    <span className="!p-3">
                        <span className="modal-icon modal-icon-close" aria-hidden="true" />
                    </span>
                </button>
                <iframe
                    ref={gameIframeRef}
                    allowFullScreen={true}
                    frameBorder={'0'}
                    src={gameUrl}
                    className="mx-auto bg-white"
                    title={'game_day_' + item.day}
                />
            </div>);
        }
    };

    const renderBadge = (day) => {
        return BADGES_BY_DAY[day] || null;
    };

    const renderCalendar = () => {
        if (!calendarData?.data?.length) {
            return null;
        }
        const dates = calendarData.data.sort(sortDesktop);
        const datesMobile = calendarDataMobile.data.sort(sortMobile);

        return (
            <>
                {/* Desktop calendar */}
                <div className="grid-cols-2 gap-[2px] w-[992px] mx-auto hidden xl:grid">
                    <div>
                        <div className="grid grid-cols-5 gap-[2px] mb-[2px]">
                            {dates.slice(0, 12).map((item, i) => {
                               // console.log(item, 'item');
                                return (<React.Fragment key={i}>
                                    {renderCell(item)}
                                </React.Fragment>)
                            })}
                        </div>
                    </div>
                    <div>
                        <div className="grid grid-cols-5 w-[496px] mx-auto gap-[2px] mb-[2px]">
                            {dates.slice(12, 24).map((item, i) => {
                                return (<React.Fragment key={i}>
                                    {renderCell(item)}
                                </React.Fragment>)
                            })}
                        </div>
                    </div>
                </div>

                {/* Mobile calendar */}
                <div className="w-full sm:max-w-[342px] md:mt-10 mx-auto xl:hidden mobile-calendar">
                    <div className="grid grid-cols-4 gap-[2px] mb-[2px]">
                        {datesMobile.slice(0, 6).map((item, i) => {
                            return (<React.Fragment key={i}>
                                {renderCell(item, true)}
                            </React.Fragment>)
                        })}
                    </div>
                    <div className="grid grid-cols-4 gap-[2px] mb-[2px] mt-[-89px]">
                        {datesMobile.slice(6, 12).map((item, i) => {
                            return (<React.Fragment key={i}>
                                {renderCell(item, true)}
                            </React.Fragment>)
                        })}
                    </div>
                    <div className="grid grid-cols-4 gap-[2px] mb-[2px] mt-[-87px]">
                        {datesMobile.slice(12, 18).map((item, i) => {
                            return (<React.Fragment key={i}>
                                {renderCell(item, true)}
                            </React.Fragment>)
                        })}
                    </div>
                    <div className="grid grid-cols-4 gap-[2px] mb-[2px] mt-[-89px]">
                        {datesMobile.slice(18, 24).map((item, i) => {
                            return (<React.Fragment key={i}>
                                {renderCell(item, true)}
                            </React.Fragment>)
                        })}
                    </div>
                </div>
            </>
        )
    };

    const renderCell = (item, mobile) => {

        let height = item.cellType === 'small' || item.cellType === 'wide' ? 'h-24' : 'h-48 row-span-2';

        const columnClass = item.cellType === 'small' || item.cellType === 'tall' ? 'col-span-1' : '';

        let active = DEBUG_MODE ? true : item.day === currentDay,
            isPreviousDay = item.day < currentDay && currentDay < 25;
        let statusCompleted = false;

       // console.log(item.day, currentDay, active, isPreviousDay);

        if (currentDay >= item.day && userData) {
            const todayPlaying = userData.filter(el => el.tournament_id === `${item.day}`);
            if (todayPlaying.length > 0 && todayPlaying.find(el => el.is_valid)) {
                statusCompleted = true;
            }
        }


        if (item.gameType && item.gameType === 'quiz') {
            if (active && statusCompleted) {
                active = false;
            }
        } else if (currentDay === item.day) {
            //statusCompleted = false;
        }

        if (mobile) { height = item.cellType === 'small' || item.cellType === 'wide' ? 'h-[83px]' : 'h-[170px]'; }
        if (active) { height = height + ' active'; }
        if (statusCompleted) { height = height + ' completed'; }

        const lay = playLayout(item.cellType, mobile);
        const sparklePosition = SPARKLE_DOOR_POSITIONS[item.day];

        return (
            <div className={`tile day-${item.day} ${TEXTURED_DOOR_DAYS.has(item.day) ? 'door-textured' : 'door-plain'} ${isPreviousDay ? 'is-previousday' : ''} ${height} ${columnClass}${openActive === item.day ? ' open' : ''}`}>
                <div className="cover relative"
                    onClick={active || (isPreviousDay && !statusCompleted) ? () => handleOpenTile(item.day) : null} >

                    {active ? (
                        <div className={`${lay.coverContainer}`}>
                            <span className={`${typeof lay.coverIcon === 'function'
                                ? lay.coverIcon(item)
                                : lay.coverIcon
                            }`}></span>
                            <span className={`${lay.coverText}`}>{lay.coverSmallText}</span>
                            <span className={`${typeof lay.coverBanner === 'function'
                                ? lay.coverBanner(item)
                                : lay.coverBanner
                            }`}></span>

                        </div>
                    ) : null}
                    {isPreviousDay ? (
                        <div className={`${lay.playedContainer}`}>
                            {/* <span className={`${lay.coverIcon}`}></span> */}
                            {/*<span className={`${lay.playedIcon}`}></span>*/}
                            {/*<span className={`${lay.playedClass}`}>{lay.playedText}</span>*/}
                        </div>
                    ) : null}
                    {!active && sparklePosition && item.cellType !== 'small' && (
                        <img
                            src={sparkles}
                            className={`door-sparkles door-sparkles-${sparklePosition}`}
                            alt=""
                            aria-hidden="true"
                        />
                    )}
                    {!active && item.day === 13 && item.cellType !== 'small' && (
                        <img
                            src={sparklesSmall}
                            className="door-sparkles-small"
                            alt=""
                            aria-hidden="true"
                        />
                    )}
                    <span className="day">{item.day}</span>
                    {statusCompleted || active || isPreviousDay
                        ? (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="193" height="28" viewBox="0 0 193 28"
                                     fill="none">
                                    <g filter="url(#filter0_f_2247_30)">
                                        <path d="M2 11.1636L191 2V26L2 12.6711V11.1636Z"
                                              fill="url(#paint0_linear_2247_30)"/>
                                    </g>
                                    <defs>
                                        <filter id="filter0_f_2247_30" x="0" y="0" width="193" height="28"
                                                filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix"
                                                     result="shape"/>
                                            <feGaussianBlur stdDeviation="1" result="effect1_foregroundBlur_2247_30"/>
                                        </filter>
                                        <linearGradient id="paint0_linear_2247_30" x1="191" y1="2" x2="2" y2="2"
                                                        gradientUnits="userSpaceOnUse">
                                            <stop stopOpacity="0.3"/>
                                            <stop offset="0.2" stopOpacity="0.6"/>
                                            <stop offset="0.4" stopOpacity="0.9"/>
                                            <stop offset="1" stopOpacity="0.9"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </>
                        )
                        : null}
                </div>
                {statusCompleted && !active ? (
                    <CompletedOverlay item={item} mobile={mobile} />
                ) : null}
                {(active || isPreviousDay) && !statusCompleted ?
                    (<button className={`title-page absolute inset-0 z-0 ${lay.container} `}
                        onClick={() => handleLoadGame(item.game, item)}>

                        {lay.showBadge && renderBadge(item.day) && (
                            <img
                                src={renderBadge(item.day)}
                                alt=""
                                className={`${lay.icon} calendar-game-badge calendar-game-badge-${item.cellType}`}
                                aria-hidden="true"
                            />
                        )}
                        {/* Play title */}
                         <span className={`${lay.text} flex items-center center`}>
                            <span className="calendar-game-icon" aria-hidden="true" />
                            <span>{isPreviousDay ? 'Play again' : 'Play now!'}</span>
                        </span>
                    </button>)
                    : null}

                {active && statusCompleted ? (
                    <ReplayOverlay item={item} mobile={mobile} />
                ) : null}
            </div>
        )
    };

    return (
        <>
            <main className='calendar-main flex-1 flex items-center justify-center'>
                <div className="container text-center pt-4 mb-[-32px] xl:py-8 xl:mb-0">
                    {renderCalendar()}
                </div>
            </main>
            <Modal
                showModal={loading}
                setShowModal={setLoading}
                classes={'modal-loader'}
                content={loaderContent}
            />
            {(gameData?.game && !gameData?.isClosedConfirmPopup) &&
                    <AlertModal
                        gameData={gameData}
                        clearGameData={handleClearGameData}
                        closeGame={closePopup}
                        loadGame={modalLoadingGame}
                        heading={'Thanks for playing!'}
                        description={'Your score has been submitted for today, so stay tuned and keep playing while you wait for the results.'}
                    />
            }

            {
                (gameData?.game && gameData?.isClosedConfirmPopup) &&
                <ConfirmedModal
                    gameData={gameData}
                    closeGame={returnToCalendar}
                    setGameData={setGameData}
                    heading={'Are you sure you want to quit?'}
                />
            }
        </>
    );
};

export default Calendar;
